"use client";

import { useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { track } from "@vercel/analytics";
import type { PublicSiteUiCopy } from "@/content/publicSiteTranslationTypes";

export default function PublicMarketplaceSearch({
  copy,
  marketplace,
}: Readonly<{
  copy: PublicSiteUiCopy["marketplace"];
  marketplace: "pilots" | "missions";
}>) {
  const [hasSearched, setHasSearched] = useState(false);

  return (
    <section
      aria-labelledby="marketplace-search-title"
      className="relative z-10 mx-auto -mt-10 max-w-6xl px-4 sm:px-6 lg:px-8"
    >
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10 sm:p-7">
        <div className="grid gap-5 lg:grid-cols-[0.8fr_1.2fr] lg:items-end">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.12em] text-accent">
              {copy.eyebrow}
            </p>
            <h2
              className="mt-2 text-2xl font-bold tracking-tight text-slate-950"
              id="marketplace-search-title"
            >
              {copy.titles[marketplace]}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {copy.description}
            </p>
          </div>
          <form
            className="flex flex-col gap-3 sm:flex-row"
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              setHasSearched(true);
              track("public_marketplace_search", {
                marketplace,
                result_state: "inventory_unavailable",
              });
              track("public_marketplace_no_results", {
                marketplace,
                reason: "inventory_unavailable",
              });
            }}
          >
            <label className="sr-only" htmlFor={`${marketplace}-public-search`}>
              {copy.labels[marketplace]}
            </label>
            <div className="relative min-w-0 flex-1">
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              />
              <input
                className="h-12 w-full rounded-lg border border-slate-300 bg-slate-50 pl-12 pr-4 text-base text-slate-950 outline-none placeholder:text-slate-500 focus:border-primary focus:bg-white focus:ring-2 focus:ring-primary/20"
                id={`${marketplace}-public-search`}
                placeholder={copy.placeholders[marketplace]}
                type="search"
              />
            </div>
            <button
              className="inline-flex h-12 items-center justify-center gap-2 rounded-lg bg-primary px-6 text-sm font-bold text-white transition hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2"
              type="submit"
            >
              <SlidersHorizontal aria-hidden="true" className="h-4 w-4" />
              {copy.button}
            </button>
          </form>
        </div>
        <div aria-live="polite">
          {hasSearched ? (
            <div className="mt-5 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm leading-6 text-blue-950">
              {copy.unavailable[marketplace]}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
